import {
  formatDeadlineDate,
  formatDeadlineUrgencyBadge,
} from '@/lib/dateUtils';
import { Button, Spinner } from '@/ui';
import { ChevronRightIcon, MailIcon } from '@/ui/icons';
import PropTypes from 'prop-types';
import SolicitudesHeader from './SolicitudesHeader.jsx';
import SolicitudesTable from './SolicitudesTable.jsx';
import './SolicitudesInbox.css';

function notifyBeforeLabel(taskDeadline) {
  const when = formatDeadlineDate(taskDeadline);
  return when ? `Notificar antes del ${when}` : '';
}

function cardDue(item) {
  if (item.taskDeadline) return notifyBeforeLabel(item.taskDeadline);
  return item.dueLabel ?? '';
}

function cardUrgency(item, now) {
  if (item.taskDeadline)
    return formatDeadlineUrgencyBadge(
      item.taskDeadline,
      now,
      item.taskDeadlineExpiresAt,
    );
  return item.urgency ?? '';
}

export default function SolicitudesInbox({
  items = [],
  onQuote,
  onReject,
  onOpen,
  onVerTareas,
  now = Date.now(),
  table = false,
  getItemHref,
  focusHeading = false,
  pending = false,
  answered = false,
  onViewChange,
}) {
  const count = items.length;

  return (
    <div className={`sol${table ? ' sol--table' : ''}`}>
      <SolicitudesHeader
        count={pending ? undefined : count}
        table={table}
        focusHeading={focusHeading}
        answered={answered}
        onViewChange={onViewChange}
      />

      {pending ? (
        <div className="sol-status" aria-busy="true">
          <Spinner aria-label="Cargando solicitudes" />
          <p className="sol-status__body">
            {answered
              ? 'Estamos buscando las solicitudes que respondiste.'
              : 'Estamos buscando las solicitudes pendientes de cotización.'}
          </p>
        </div>
      ) : count === 0 && answered ? (
        <div className="sol-empty">
          <div className="sol-empty__icon" aria-hidden="true">
            <MailIcon size={28} />
          </div>
          <h2 className="sol-empty__title">
            Todavía no has respondido solicitudes
          </h2>
          <p className="sol-empty__body">
            Las que cotices o rechaces, y las que se cierren, quedan acá.
          </p>
        </div>
      ) : count === 0 ? (
        <div className="sol-empty">
          <div className="sol-empty__icon" aria-hidden="true">
            <MailIcon size={28} />
          </div>
          <h2 className="sol-empty__title">No hay solicitudes pendientes</h2>
          <p className="sol-empty__body">
            Cuando un estudio te pide una cotización, aparece acá para cotizar o
            rechazar.
          </p>
          {onVerTareas ? (
            <Button variant="secondary" size="md" onClick={onVerTareas}>
              Ver tareas
            </Button>
          ) : null}
        </div>
      ) : table ? (
        <SolicitudesTable
          key={answered ? 'respondidas' : 'pendientes'}
          items={items}
          answered={answered}
          now={now}
          getItemHref={getItemHref}
          onOpen={onOpen}
        />
      ) : (
        <ul className="sol-list">
          {items.map((item) => {
            // An answered quote has nothing left to rush.
            const urgency = answered ? '' : cardUrgency(item, now);
            const due = cardDue(item);
            return (
              <li key={item.id} className="sol-card">
                <button
                  type="button"
                  className="sol-card__open"
                  onClick={() => onOpen?.(item)}
                >
                  <span className="sol-card__meta">
                    <span className="sol-card__rol">{item.rol}</span>
                    {urgency ? (
                      <span className="sol-card__badge">{urgency}</span>
                    ) : null}
                    <ChevronRightIcon size={16} />
                  </span>
                  <span className="sol-card__party">{item.party}</span>
                  <span className="sol-card__kind">
                    {item.tipo}
                    {item.comuna ? ` · ${item.comuna}` : ''}
                  </span>
                  {answered ? (
                    <span className="sol-card__price">
                      {[item.answerLabel, item.offeredPrice]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : (
                    <span className="sol-card__price">
                      {item.priceLabel ?? 'Por cotizar'}
                    </span>
                  )}
                  {due ? <span className="sol-card__due">{due}</span> : null}
                </button>
                {!answered ? (
                  <div className="sol-card__actions">
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => onReject?.(item)}
                    >
                      Rechazar
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => onQuote?.(item)}
                    >
                      Cotizar
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

SolicitudesInbox.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      rol: PropTypes.string,
      party: PropTypes.string,
      tipo: PropTypes.string,
      comuna: PropTypes.string,
      priceLabel: PropTypes.string,
      answerLabel: PropTypes.string,
      offeredPrice: PropTypes.string,
      taskDeadline: PropTypes.string,
      taskDeadlineExpiresAt: PropTypes.string,
      dueLabel: PropTypes.string,
      urgency: PropTypes.string,
    }),
  ),
  onQuote: PropTypes.func,
  onReject: PropTypes.func,
  onOpen: PropTypes.func,
  onVerTareas: PropTypes.func,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  table: PropTypes.bool,
  getItemHref: PropTypes.func,
  focusHeading: PropTypes.bool,
  pending: PropTypes.bool,
  answered: PropTypes.bool,
  onViewChange: PropTypes.func,
};
